import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const SIDEBAR_STORAGE_KEY = "sidebarOpen";
const DESKTOP_QUERY = "(min-width: 768px)";

export interface SidebarContextValue {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const [sidebarOpen, setSidebarOpenState] = useState(false);

  useEffect(() => {
    if (isDesktop) {
      const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
      setSidebarOpenState(stored === null ? true : stored === "true");
    } else {
      setSidebarOpenState(false);
    }
  }, [isDesktop]);

  const setSidebarOpen = (open: boolean) => {
    setSidebarOpenState(open);
    if (isDesktop) {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(open));
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const value: SidebarContextValue = {
    sidebarOpen,
    setSidebarOpen,
    toggleSidebar
  };

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar() {
  const value = useContext(SidebarContext);
  if (!value) {
    throw new Error("useSidebar must be used within SidebarProvider");
  }

  return value;
}
